import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { DeliveryPace, DeliveryReport, DeliverySeries } from "@aura/shared";
import { css } from "styled-system/css";
import { Figure, Ledger } from "~/components/charts";
import { ed } from "~/components/editorial";
import { api } from "~/lib/api";
import { fmtInt, fmtNum, fmtPct } from "~/lib/format";
import { MergedColumns } from "./charts";
import { mergedTable } from "./series";
import { botsQuery, useBotsParam, useCompareParam } from "./lib";
import { BotsToggle, Byline, Controls, ReportShell, Segments } from "./parts";
import {
  deliveryDek,
  deliveryHeadline,
  findings,
  paceDek,
  paceHeadline,
  percentChange,
  pointsChange,
} from "./story";

const REPORTS = [
  { to: "/delivery/trends", title: "Trends", detail: "Every period since history starts: merged PRs, how they were checked, and speed, by week, month or quarter." },
  { to: "/delivery/pace", title: "Pace", detail: "This quarter so far against the same days of the last two, with a projection." },
  { to: "/delivery/breakdown", title: "Breakdown", detail: "By org, product and tier, and repo, for the last two complete months or quarters. CSV per table." },
  { to: "/delivery/authors", title: "By author", detail: "Per-person counts, for finding where to look." },
];

// The overview: what happened in the last complete period, how this quarter
// is pacing, and what stands out, with a way into each detailed report.
export function OverviewPage() {
  const { includeBots, setIncludeBots } = useBotsParam();
  const [compare, setCompare] = useCompareParam();
  const bots = botsQuery(includeBots);

  const report = useQuery({
    queryKey: ["delivery", compare, includeBots],
    queryFn: () => api.get<{ data: DeliveryReport }>(`/delivery?compare=${compare}${bots}`),
    staleTime: 5 * 60 * 1000,
  });
  const series = useQuery({
    queryKey: ["delivery-series", compare, "pxe", includeBots],
    queryFn: () => api.get<{ data: DeliverySeries }>(`/delivery/series?grain=${compare}&by=pxe${bots}`),
    staleTime: 5 * 60 * 1000,
  });
  const pace = useQuery({
    queryKey: ["delivery-pace", includeBots],
    queryFn: () => api.get<{ data: DeliveryPace }>(`/delivery/pace?${bots.slice(1)}`),
    staleTime: 5 * 60 * 1000,
  });

  const r = report.data?.data;
  const s = series.data?.data;
  const p = pace.data?.data;

  return (
    <ReportShell meta={r ? `${r.compare.current.label} vs. ${r.compare.previous.label}` : undefined}>
      <Controls>
        <Segments
          label="Compare"
          value={compare}
          onChange={setCompare}
          options={[
            { value: "month", label: "Month" },
            { value: "quarter", label: "Quarter" },
          ]}
        />
        <BotsToggle includeBots={includeBots} onChange={setIncludeBots} />
      </Controls>

      {report.isLoading ? (
        <div className={ed.loading}>Loading the delivery overview…</div>
      ) : !r ? (
        <div className={ed.loading}>Couldn't load the delivery overview.</div>
      ) : (
        <>
          <div className={ed.kicker}>
            Engineering delivery · {r.compare.current.label} vs. {r.compare.previous.label}
          </div>
          <h1 className={ed.headline}>{deliveryHeadline(r)}</h1>
          <p className={ed.dek}>{deliveryDek(r)}</p>
          <Byline title="Delivery overview" meta={r} />

          <Ledger
            columns={4}
            items={[
              {
                label: `PxE PRs merged, ${r.compare.current.short}`,
                current: r.headline.current.merged,
                prior: r.headline.previous.merged,
                ...percentChange(r.headline.current.merged, r.headline.previous.merged),
              },
              {
                label: "Checked before merge",
                value: fmtPct(r.headline.current.checkedPct),
                prior: fmtPct(r.headline.previous.checkedPct),
                ...pointsChange(r.headline.current.checkedPct, r.headline.previous.checkedPct),
                note: `${fmtPct(r.headline.current.reviewedPct)} reviewed`,
              },
              {
                label: "Median hours to 1st review",
                value: fmtNum(r.headline.current.medianHoursToFirstReview),
                prior: fmtNum(r.headline.previous.medianHoursToFirstReview),
                ...percentChange(r.headline.current.medianHoursToFirstReview, r.headline.previous.medianHoursToFirstReview),
              },
              {
                label: "Median hours to merge",
                value: fmtNum(r.headline.current.medianHoursToMerge),
                prior: fmtNum(r.headline.previous.medianHoursToMerge),
                ...percentChange(r.headline.current.medianHoursToMerge, r.headline.previous.medianHoursToMerge),
              },
            ]}
          />
          <p className={ed.methodNarrow}>
            {r.compare.current.label} against {r.compare.previous.label}, both complete. PxE is every
            product except Outside PxE (Marketplace). Checked: reviewed by someone other than the author before the merge, merged by a person other than the author, or a GitHub Actions run passed on the PR before the merge. {r.compare.toDate.label} so far:{" "}
            <span className="num">{fmtInt(r.toDate.merged)}</span> PxE PRs in {r.compare.toDate.days}{" "}
            of {r.compare.toDate.totalDays} days, {fmtPct(r.toDate.checkedPct)} checked.
          </p>

          <div className={ed.sect}>I. What stands out</div>
          <ol className={styles.findings}>
            {findings(r, s).map((f) => (
              <li key={f.key}>
                <span className={ed.prose}>{f.text}</span>{" "}
                <Link to={f.to} className={styles.more}>
                  Details
                </Link>
              </li>
            ))}
          </ol>

          {p && (
            <>
              <div className={ed.sect}>II. This quarter</div>
              <h2 className={ed.h2}>{paceHeadline(p)}</h2>
              <p className={ed.prose}>{paceDek(p)}</p>
              <Ledger
                columns={4}
                hideMissingPrior
                items={[
                  { label: `${p.quarter.short} so far`, value: fmtInt(p.pxe.toDate.merged), note: `day ${p.quarter.days} of ${p.quarter.totalDays}` },
                  { label: `${p.lastQuarter.short} by day ${p.quarter.days}`, value: fmtInt(p.pxe.lastToDate.merged) },
                  { label: `${p.quarter.short} projected`, value: p.pxe.projected === null ? "–" : fmtInt(p.pxe.projected), note: p.tooEarly ? "too early" : "straight line" },
                  { label: `${p.lastQuarter.short} total`, value: fmtInt(p.pxe.lastTotal) },
                ]}
              />
              <p className={ed.methodNarrow}>
                <Link to="/delivery/pace">The pace report</Link> has the running totals and pace by
                product and org.
              </p>
            </>
          )}

          {s && (
            <>
              <div className={ed.sect}>III. The long view</div>
              <Figure
                title={`PxE PRs merged each ${compare}, since ${s.periods[0]?.label ?? "history starts"}`}
                sub="Ink is checked before merge, gray merged with no check. The last column is the period in progress."
                method={`Merged PxE PRs by calendar ${compare} (UTC). An asterisk marks a period that starts before every org's history does. Switch to weeks on the Trends report.`}
                table={mergedTable(s)}
              >
                <MergedColumns series={s} />
              </Figure>
            </>
          )}

          <div className={ed.sect}>IV. The reports</div>
          <ul className={styles.reports}>
            {REPORTS.map((x) => (
              <li key={x.to}>
                <Link to={x.to} className={styles.reportTitle}>
                  {x.title}
                </Link>
                <span className={styles.reportDetail}>{x.detail}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </ReportShell>
  );
}

const styles = {
  findings: css({
    listStyle: "decimal",
    pl: "22px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    mt: "8px",
    maxWidth: "760px",
    "& li::marker": { fontFamily: "mono", color: "ed.ink3" },
  }),
  more: css({
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    color: "ed.ink2",
    whiteSpace: "nowrap",
  }),
  reports: css({
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    mt: "8px",
    "& li": {
      display: "flex",
      flexWrap: "wrap",
      gap: "4px 14px",
      alignItems: "baseline",
      borderBottom: "1px solid",
      borderColor: "ed.grid",
      pb: "8px",
    },
  }),
  reportTitle: css({ fontFamily: "serif", fontSize: "18px", fontWeight: 700, minWidth: "120px" }),
  reportDetail: css({ fontFamily: "sans", fontSize: "13px", color: "ed.ink2" }),
};
